import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import type { TrainingOption } from '@hcc/types';

/**
 * Drag-to-order answer. Value is the item ids in the user's chosen order
 * (seeded from the server-shuffled list). dnd-kit gives finger drag on
 * tablets; the up/down buttons are the always-available keyboard/touch
 * fallback.
 */
export function OrderAnswer({
  items,
  value,
  onChange,
  disabled,
  ariaLabelledBy,
}: {
  items: TrainingOption[];
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
}) {
  const labelById = new Map(items.map((i) => [i.id, i.label]));
  const order = value && value.length === items.length ? value : items.map((i) => i.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    onChange(arrayMove(order, from, to));
  }

  function nudge(index: number, delta: -1 | 1) {
    const to = index + delta;
    if (to < 0 || to >= order.length) return;
    onChange(arrayMove(order, index, to));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy} disabled={disabled}>
        <ol aria-labelledby={ariaLabelledBy} className="flex flex-col gap-2">
          {order.map((id, index) => (
            <OrderRow
              key={id}
              id={id}
              index={index}
              last={index === order.length - 1}
              label={labelById.get(id) ?? id}
              disabled={disabled}
              onNudge={nudge}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function OrderRow({
  id,
  index,
  last,
  label,
  disabled,
  onNudge,
}: {
  id: string;
  index: number;
  last: boolean;
  label: string;
  disabled?: boolean;
  onNudge: (index: number, delta: -1 | 1) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex min-h-[44px] items-center gap-2 rounded-token-sm border border-line/10 bg-surface px-3 py-2 text-sm text-fg ${
        isDragging ? 'z-10 shadow-token border-accent' : ''
      }`}
    >
      <span
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder: ${label}`}
        className={`touch-none text-fg-dim ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}`}
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="w-5 shrink-0 text-xs font-semibold tabular-nums text-fg-muted">
        {index + 1}.
      </span>
      <span className="flex-1">{label}</span>
      <span className="flex shrink-0 gap-1">
        <button
          type="button"
          aria-label={`Move ${label} up`}
          disabled={disabled || index === 0}
          onClick={() => onNudge(index, -1)}
          className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={`Move ${label} down`}
          disabled={disabled || last}
          onClick={() => onNudge(index, 1)}
          className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </span>
    </li>
  );
}
